// The video looks. Orbit, Glide, Neon, Marble and Insta are the old
// server-rendered templates (the external video API's compositions),
// rebuilt to their preview renders: positions and sizes below are measured
// off those 1080x1920 frames, so `u` is one pixel of that frame. Spotlight
// is the studio's own, as are Karaoke, Poster, Call and Notes (2026-10).
// Each draws the template's layer at song time `t` over the background;
// Spotlight and Glide leave the lyrics to the caption layer, the others set
// the lyrics themselves.
import { phrasesOf, type CaptionWord, type Phrase } from "./layers";
import { stretchAt, type VideoProject } from "./project";
import { DISPLAY, SANS } from "./fonts";
import { ENVELOPE_RATE } from "./audio";
import { songTitle } from "@/utils/songTitle";
import type { Scene, Singer } from "./frame";

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export type TemplateId =
  | "spotlight"
  | "orbit"
  | "glide"
  | "neon"
  | "scroll"
  | "chat"
  | "karaoke"
  | "poster"
  | "call"
  | "notes";

export const TEMPLATES: {
  id: TemplateId;
  name: string;
  note: string;
  background: VideoProject["background"]["kind"];
  captions: boolean;
}[] = [
  {
    id: "spotlight",
    name: "Spotlight",
    note: "The voice, big, with lyrics",
    background: "voice",
    captions: true,
  },
  {
    id: "orbit",
    name: "Orbit",
    note: "Every voice, with the line being sung",
    background: "stars",
    captions: false,
  },
  {
    id: "glide",
    name: "Glide",
    note: "A music player and a spinning record",
    background: "embers",
    captions: true,
  },
  {
    id: "neon",
    name: "Neon",
    note: "One huge word at a time",
    background: "fluid",
    captions: false,
  },
  {
    id: "scroll",
    name: "Marble",
    note: "Lyrics scrolling past",
    background: "stars",
    captions: false,
  },
  {
    id: "chat",
    name: "Insta",
    note: "Lyrics as messages from each voice",
    background: "stars",
    captions: false,
  },
  {
    id: "karaoke",
    name: "Karaoke",
    note: "Big lyrics wiping in, a bouncing ball on the beat",
    background: "voice",
    captions: false,
  },
  {
    id: "poster",
    name: "Poster",
    note: "Each line as huge stacked type",
    background: "gradient",
    captions: false,
  },
  {
    id: "call",
    name: "Call",
    note: "A video call from the voice, lyrics as captions",
    background: "voice",
    captions: false,
  },
  {
    id: "notes",
    name: "Notes",
    note: "The lyrics typed out in a notes app",
    background: "gradient",
    captions: false,
  },
];

export const templateNamed = (id: TemplateId) =>
  TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0];

// ── shared helpers ─────────────────────────────────────────────────────────

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const easeOut = (v: number) => 1 - Math.pow(1 - clamp01(v), 3);
const easeInOut = (v: number) => {
  const x = clamp01(v);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};

/** One pixel of the 1080x1920 frame the templates were designed on, fitted to this frame. */
const unit = (W: number, H: number) => Math.min(W / 1080, H / 1350);

/** Who is singing at `t`: the solo voice, or the duet's current turn (else the last one). */
export function singerAt(
  scene: Scene,
  t: number,
): { singer: Singer | null; index: number; since: number } {
  if (scene.singers.length <= 1)
    return {
      singer: scene.singers[0] ?? null,
      index: 0,
      since: t - scene.project.trim.start,
    };
  const n = stretchAt(scene.voiceTimes, t);
  const stretch =
    n >= 0
      ? scene.voiceTimes[n]
      : [...scene.voiceTimes].reverse().find((s) => s.start <= t);
  const index = stretch
    ? scene.singers.findIndex((s) => s.urlParam === stretch.voice)
    : 0;
  return {
    singer: scene.singers[Math.max(0, index)] ?? null,
    index: Math.max(0, index),
    since: stretch ? t - stretch.start : t,
  };
}

function photoIn(
  ctx: Ctx,
  singer: Singer | null,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const photo = singer?.photo;
  if (photo) {
    const k = Math.max(w / photo.width, h / photo.height);
    ctx.drawImage(
      photo,
      x + (w - photo.width * k) / 2,
      y + (h - photo.height * k) / 2,
      photo.width * k,
      photo.height * k,
    );
  } else {
    ctx.fillStyle = singer?.color ?? "#782DC8";
    ctx.fillRect(x, y, w, h);
  }
}

function circlePhoto(
  ctx: Ctx,
  singer: Singer | null,
  cx: number,
  cy: number,
  r: number,
) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  photoIn(ctx, singer, cx - r, cy - r, 2 * r, 2 * r);
  ctx.restore();
}

/** Greedy word wrap at the context's current font. */
function wrap(ctx: Ctx, text: string, width: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(next).width > width) {
      lines.push(line);
      line = word;
    } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}

const phraseCache = new WeakMap<CaptionWord[], Phrase[]>();
function phrases(words: CaptionWord[]): Phrase[] {
  let p = phraseCache.get(words);
  if (!p) phraseCache.set(words, (p = phrasesOf(words)));
  return p;
}

/** The phrase on screen at `t`: the last one started, held until the next. */
function phraseIndexAt(list: Phrase[], t: number): number {
  let idx = -1;
  for (let i = 0; i < list.length; i++) {
    if (list[i].start <= t) idx = i;
    else break;
  }
  return idx;
}

/** The words of a phrase sung by `t`, for text that writes itself as it's sung. */
function sungSoFar(words: CaptionWord[], ph: Phrase, t: number): string {
  return words
    .slice(ph.first, ph.last + 1)
    .filter((w) => w.start <= t)
    .map((w) => w.text)
    .join(" ");
}

const lyricsOn = (scene: Scene) =>
  scene.project.captions.on && scene.words.length > 0;

/** How loud the song is at t, 0..1, lightly smoothed (for things that pulse). */
function loudnessAt(scene: Scene, t: number): number {
  const env = scene.envelope;
  if (!env || !env.length) return 0;
  const i = Math.floor(t * ENVELOPE_RATE);
  let sum = 0,
    n = 0;
  for (let k = i - 2; k <= i + 1; k++)
    if (k >= 0 && k < env.length) {
      sum += env[k];
      n++;
    }
  return n ? sum / n : 0;
}

/**
 * Glide's movement, shared with Orbit: the layer fades and rises in over
 * 0.7s, then rocks gently for the rest of the clip, a small turn with a
 * hint of perspective. Leaves the origin at (cx, cy).
 */
function glideMotion(
  ctx: Ctx,
  cx: number,
  cy: number,
  since: number,
  u: number,
) {
  const enter = easeOut(since / 0.7);
  ctx.globalAlpha = enter;
  ctx.translate(cx, cy + (1 - enter) * 60 * u);
  const rock = Math.sin(since * 0.9);
  ctx.transform(1, rock * 0.012, rock * -0.01, 1, 0, 0);
  ctx.rotate(rock * 0.022);
}

// ── Orbit ──────────────────────────────────────────────────────────────────
// A column of round photos down the left, white rings, names in caps under
// them; the line being sung sits beside its singer, in caps.

function drawOrbit(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  const u = unit(W, H);
  const singers = scene.singers.length ? scene.singers : [null];
  const r = 99 * u;
  const step = Math.min(285 * u, (H * 0.84) / Math.max(1, singers.length));
  const scale = Math.min(1, step / (285 * u));
  const x = 198 * u;
  const top = H / 2 - ((singers.length - 1) * step) / 2;
  const now = singerAt(scene, t);
  const list = phrases(scene.words);
  const p = phraseIndexAt(list, t);

  // The whole column moves as one, pivoting between the photos and the line.
  const pivotX = W * 0.35;
  ctx.save();
  glideMotion(ctx, pivotX, H / 2, t - scene.project.trim.start, u);
  ctx.translate(-pivotX, -H / 2);

  singers.forEach((s, i) => {
    const cy = top + i * step;
    const rr = r * scale;
    ctx.beginPath();
    ctx.arc(x, cy, rr + 6 * u, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    circlePhoto(ctx, s, x, cy, rr);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `800 ${Math.round(26 * u * scale)}px ${SANS}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      (s?.name ?? "").toUpperCase(),
      x,
      cy + rr + 32 * u * scale,
      260 * u,
    );

    if (i === now.index && lyricsOn(scene) && p >= 0) {
      const since = t - list[p].start;
      ctx.save();
      ctx.globalAlpha = easeOut(since / 0.18);
      ctx.fillStyle = scene.project.captions.color;
      ctx.font = `700 ${Math.round(44 * u)}px ${SANS}`;
      ctx.textAlign = "left";
      const left = x + rr + 18 * u;
      const lines = wrap(
        ctx,
        list[p].text.toUpperCase(),
        W - left - 60 * u,
      ).slice(0, 3);
      const lh = 54 * u;
      lines.forEach((line, k) =>
        ctx.fillText(line, left, cy + (k - (lines.length - 1) / 2) * lh),
      );
      ctx.restore();
    }
  });
  ctx.restore();
}

// ── Glide ──────────────────────────────────────────────────────────────────
// A lavender music player: the art in a white frame, title, artist, a
// progress bar and the controls, with a vinyl sliding out from behind it
// and spinning. The card glides in and rocks gently the whole way.

function drawRecord(ctx: Ctx, singer: Singer | null, R: number, spin: number) {
  ctx.save();
  ctx.rotate(spin);
  const body = ctx.createRadialGradient(0, 0, R * 0.2, 0, 0, R);
  body.addColorStop(0, "#1A1A1A");
  body.addColorStop(1, "#050505");
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(0, 0, R, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#3A3A3A";
  ctx.lineWidth = R * 0.018;
  for (const g of [0.56, 0.68, 0.8, 0.92]) {
    ctx.beginPath();
    ctx.arc(0, 0, R * g, 0, Math.PI * 2);
    ctx.stroke();
  }
  circlePhoto(ctx, singer, 0, 0, R * 0.44);
  // The sheen that makes it read as vinyl as it turns.
  const sheen = ctx.createLinearGradient(-R, -R, R, R);
  sheen.addColorStop(0.3, "rgba(255,255,255,0)");
  sheen.addColorStop(0.5, "rgba(255,255,255,0.12)");
  sheen.addColorStop(0.7, "rgba(255,255,255,0)");
  ctx.fillStyle = sheen;
  ctx.beginPath();
  ctx.arc(0, 0, R, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Elapsed / remaining under the bar, as a player shows them.
const mmss = (secs: number) => {
  const s = Math.max(0, Math.round(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

function drawGlide(
  ctx: Ctx,
  W: number,
  H: number,
  scene: Scene,
  t: number,
  title: string,
) {
  const u = unit(W, H);
  const { trim } = scene.project;
  const since = t - trim.start;
  const length = Math.max(0.1, trim.end - trim.start);
  const cw = 660 * u,
    ch = 894 * u;
  const R = 296 * u;
  // The record ends with its centre on the card's right edge: half of it
  // out. The pair (card + half a record) is centred in the frame.
  const cardCx = W / 2 - R / 2,
    cardCy = H / 2;
  const now = singerAt(scene, t);
  const enter = easeOut(since / 0.7);
  const slide = easeInOut((since - 0.35) / 0.9);

  ctx.save();
  glideMotion(ctx, cardCx, cardCy, since, u);

  // The record, behind the card: from tucked away to half out.
  const hidden = cw / 2 - R * 1.05,
    out = cw / 2;
  ctx.save();
  ctx.translate(hidden + (out - hidden) * slide, -ch / 2 + 448 * u);
  drawRecord(ctx, now.singer, R, since * Math.PI * 2 * 0.5);
  ctx.restore();

  const left = -cw / 2,
    top = -ch / 2;
  const radius = 64 * u;

  // The card: a soft lavender gradient, a glow behind, a highlight on top.
  ctx.save();
  ctx.shadowColor = "rgba(255, 255, 255, 0.45)";
  ctx.shadowBlur = 56 * u;
  const body = ctx.createLinearGradient(0, top, 0, top + ch);
  body.addColorStop(0, "#A387D6");
  body.addColorStop(1, "#7E5DB8");
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.roundRect(left, top, cw, ch, radius);
  ctx.fill();
  ctx.restore();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.28)";
  ctx.lineWidth = 2 * u;
  ctx.beginPath();
  ctx.roundRect(left + u, top + u, cw - 2 * u, ch - 2 * u, radius);
  ctx.stroke();

  // The art, in a white frame.
  const art = 505 * u,
    ax = left + 77 * u,
    ay = top + 64 * u,
    frame = 12 * u;
  ctx.save();
  ctx.shadowColor = "rgba(46, 15, 82, 0.35)";
  ctx.shadowBlur = 30 * u;
  ctx.shadowOffsetY = 12 * u;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.roundRect(
    ax - frame,
    ay - frame,
    art + frame * 2,
    art + frame * 2,
    44 * u,
  );
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(ax, ay, art, art, 34 * u);
  ctx.clip();
  photoIn(ctx, now.singer, ax, ay, art, art);
  ctx.restore();

  // Title and artist.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `800 ${Math.round(34 * u)}px ${SANS}`;
  ctx.fillText(title, 0, top + 636 * u, cw - 120 * u);
  ctx.globalAlpha = enter * 0.75;
  ctx.font = `600 ${Math.round(24 * u)}px ${SANS}`;
  ctx.fillText(
    scene.singers.map((s) => s.name).join(" & "),
    0,
    top + 680 * u,
    cw - 120 * u,
  );
  ctx.globalAlpha = enter;

  // Progress through the clip: a faint track, the played part, the times.
  const bx0 = left + 92 * u,
    bx1 = left + cw - 92 * u,
    by = top + 728 * u;
  const prog = clamp01(since / length);
  ctx.lineCap = "round";
  ctx.lineWidth = 8 * u;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
  ctx.beginPath();
  ctx.moveTo(bx0, by);
  ctx.lineTo(bx1, by);
  ctx.stroke();
  ctx.strokeStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.moveTo(bx0, by);
  ctx.lineTo(bx0 + (bx1 - bx0) * prog, by);
  ctx.stroke();
  ctx.globalAlpha = enter * 0.75;
  ctx.font = `600 ${Math.round(19 * u)}px ${SANS}`;
  ctx.textAlign = "left";
  ctx.fillText(mmss(since), bx0, by + 30 * u);
  ctx.textAlign = "right";
  ctx.fillText(`-${mmss(length - since)}`, bx1, by + 30 * u);
  ctx.globalAlpha = enter;

  // Controls: back, pause (a white button), forward, all solid.
  const cy = top + 812 * u;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(0, cy, 46 * u, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#7E5DB8";
  for (const bar of [-1, 1]) {
    ctx.beginPath();
    ctx.roundRect(bar * 10 * u - 5 * u, cy - 16 * u, 10 * u, 32 * u, 4 * u);
    ctx.fill();
  }
  ctx.fillStyle = "#FFFFFF";
  ctx.lineJoin = "round";
  for (const dir of [-1, 1]) {
    const bx = dir * 120 * u,
      s = 15 * u;
    for (const k of [0, 1]) {
      const x0 = bx + dir * (k * s * 1.1 - s * 0.9);
      ctx.beginPath();
      ctx.moveTo(x0, cy - s);
      ctx.lineTo(x0 + dir * s * 1.2, cy);
      ctx.lineTo(x0, cy + s);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

// ── Neon ───────────────────────────────────────────────────────────────────
// One word at a time in a chunky display face, white with a pink extrude.

function drawNeon(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  if (!lyricsOn(scene)) return drawBigWord(ctx, W, H, "JAMMABLE", 1, scene);
  const words = scene.words;
  let i = -1;
  for (let k = 0; k < words.length; k++) {
    if (words[k].start <= t) i = k;
    else break;
  }
  if (i < 0) return;
  const w = words[i];
  if (
    t > Math.max(w.end + 1, w.start + 0.25) &&
    (i + 1 >= words.length || words[i + 1].start > t)
  )
    return;
  drawBigWord(
    ctx,
    W,
    H,
    w.text.replace(/[^\p{L}\p{N}'’!?-]/gu, "").toUpperCase(),
    easeOut((t - w.start) / 0.14),
    scene,
  );
}

function drawBigWord(
  ctx: Ctx,
  W: number,
  H: number,
  text: string,
  pop: number,
  scene: Scene,
) {
  if (!text) return;
  const u = unit(W, H);
  let size = 150 * u;
  ctx.font = `400 ${size}px ${DISPLAY}`;
  const fit = (W * 0.86) / Math.max(1, ctx.measureText(text).width);
  if (fit < 1) size *= fit;
  ctx.save();
  ctx.translate(W / 2, H * 0.5);
  const k = 0.82 + 0.18 * pop;
  ctx.scale(k, k);
  ctx.globalAlpha = clamp01(pop * 1.6);
  ctx.font = `400 ${size}px ${DISPLAY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // The extrude: the word stacked down-right in pink, then white on top.
  const depth = Math.max(2, Math.round(size * 0.075));
  ctx.fillStyle =
    scene.project.captions.accent && scene.project.captions.accent !== "#C4FF00"
      ? scene.project.captions.accent
      : "#D6398C";
  for (let d = depth; d > 0; d--) ctx.fillText(text, d * 0.9, d);
  ctx.fillStyle = scene.project.captions.color;
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

// ── Marble ─────────────────────────────────────────────────────────────────
// The lyrics as a tight column with depth: it holds on the line being sung,
// then swings to the next one. The line being sung is big and sharp, in the
// caption colour, with a soft glow; the lines around it sit further back,
// smaller, fainter and more blurred the further they are.

// The column holds on the line being sung, then swings to the next one,
// starting just before it's sung so the words land as it settles.
const SWING_LEAD = 0.12;
const SWING_SECS = 0.7;

/** The swing: a smooth start and a long, soft landing, no overshoot. */
function swingEase(x: number): number {
  const v = clamp01(x);
  return v < 0.5 ? 4 * v * v * v : 1 - Math.pow(-2 * v + 2, 3) / 2;
}

/** Which line the column is on at t, in lines (2.5 = mid-swing from 2 to 3). */
function columnAt(list: Phrase[], t: number): number {
  let p = 0;
  for (let k = 1; k < list.length; k++)
    if (list[k].start - SWING_LEAD <= t) p = k;
    else break;
  if (p === 0) return 0;
  return p - 1 + swingEase((t - (list[p].start - SWING_LEAD)) / SWING_SECS);
}

// Depth: the line in focus is full size and sharp; the lines either side
// are SIDE_SCALE, and each line further out is smaller, fainter and more
// blurred, as if further back.
const SIDE_SCALE = 0.48;
const FAR_SCALE = 0.36;
const DEPTH_LINES = 4;

function drawScroll(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  if (!lyricsOn(scene)) return;
  const u = unit(W, H);
  const list = phrases(scene.words);
  if (!list.length) return;
  // Big and narrow, so the line in focus usually runs to two or three rows.
  const size = Math.round(150 * u);
  ctx.font = `800 ${size}px ${SANS}`;
  const left = W * 0.09,
    width = W * 0.8,
    lh = size * 1.04,
    gap = 34 * u;
  const blocks = list.map((ph) => wrap(ctx, ph.text, width));
  const pos = columnAt(list, t);
  // Size by distance from the focus, in lines: full at the focus, shrinking
  // to SIDE_SCALE one line away. Laid out each frame from these sizes, so
  // the column stays tight as lines grow and shrink through the swing.
  const scaleOf = (k: number) => {
    const d = Math.abs(k - pos);
    return d <= 1
      ? SIDE_SCALE + (1 - SIDE_SCALE) * (1 - d)
      : Math.max(FAR_SCALE, SIDE_SCALE - 0.06 * (d - 1));
  };
  const heightOf = (k: number) => blocks[k].length * lh * scaleOf(k);
  const step = (a: number, b: number) =>
    heightOf(a) / 2 + gap + heightOf(b) / 2;
  const k0 = Math.min(
      Math.max(0, Math.floor(pos)),
      Math.max(0, list.length - 2),
    ),
    k1 = Math.min(list.length - 1, k0 + 1);
  const centre: Record<number, number> = {};
  centre[k0] = H * 0.5 - (k1 > k0 ? step(k0, k1) * (pos - k0) : 0);
  for (let k = k0 + 1; k < Math.min(list.length, k0 + DEPTH_LINES + 2); k++)
    centre[k] = centre[k - 1] + step(k - 1, k);
  for (let k = k0 - 1; k >= Math.max(0, k0 - DEPTH_LINES - 1); k--)
    centre[k] = centre[k + 1] - step(k, k + 1);
  const colour = scene.project.captions.color;

  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  // Furthest first, so the line in focus is drawn in front.
  const order = Object.keys(centre)
    .map(Number)
    .sort((a, b) => Math.abs(b - pos) - Math.abs(a - pos));
  for (const k of order) {
    const dist = Math.abs(k - pos);
    // Fainter with each line back: 1 at the focus, ~0.5 beside it, then
    // trailing off to nothing DEPTH_LINES away.
    const fade =
      dist <= 1
        ? 1 - 0.5 * dist
        : 0.5 * clamp01(1 - (dist - 1) / (DEPTH_LINES - 1));
    if (fade <= 0.01) continue;
    // How in focus it is, from the same swing as its position and size, so
    // it grows, brightens and takes the caption colour as one movement.
    const focus = clamp01(1 - dist);
    const scale = scaleOf(k);
    ctx.save();
    ctx.translate(left, centre[k]);
    ctx.scale(scale, scale);
    ctx.globalAlpha = fade * (0.55 + 0.45 * focus);
    // Out of focus: blurred more the further back (canvas filters are
    // missing in some browsers, which then just draw it sharp).
    const blur = Math.min(dist, DEPTH_LINES) * 3.2 * u;
    if (blur > 0.3 * u) ctx.filter = `blur(${blur.toFixed(1)}px)`;
    const lines = blocks[k];
    const text = () =>
      lines.forEach((line, j) =>
        ctx.fillText(line, 0, (j - (lines.length - 1) / 2) * lh),
      );
    ctx.fillStyle = "#FFFFFF";
    text();
    // The caption colour fades in over the white, with its glow.
    if (focus > 0.01 && colour.toUpperCase() !== "#FFFFFF") {
      ctx.globalAlpha *= focus;
      ctx.fillStyle = colour;
      ctx.shadowColor = colour;
      ctx.shadowBlur = 30 * u * focus;
      text();
    } else if (focus > 0.01) {
      ctx.shadowColor = colour;
      ctx.shadowBlur = 30 * u * focus;
      ctx.globalAlpha *= focus * 0.6;
      text();
    }
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

// ── Insta ──────────────────────────────────────────────────────────────────
// Each line arrives as a message: the singer's photo in a story ring, their
// name, and the words appearing as they're sung. Older ones move up.

function drawChat(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  if (!lyricsOn(scene)) return;
  const u = unit(W, H);
  const list = phrases(scene.words);
  const p = phraseIndexAt(list, t);
  if (p < 0) return;
  const row = 280 * u,
    cx = 202 * u,
    R = 98 * u,
    ringW = 7 * u,
    inner = 85 * u;
  const tx = 324 * u;
  const VISIBLE = 3;
  // Rows fill down from 42%; once three are showing, the list scrolls up.
  const scrollTo = (k: number) => Math.max(0, k - (VISIBLE - 1));
  const shift =
    scrollTo(p - 1) +
    (scrollTo(p) - scrollTo(p - 1)) * easeInOut((t - list[p].start) / 0.4);
  const ring = ctx.createLinearGradient(-R, R, R, -R);
  ring.addColorStop(0, "#F9A043");
  ring.addColorStop(0.55, "#EE3A6E");
  ring.addColorStop(1, "#D6249F");

  for (let k = Math.max(0, p - VISIBLE); k <= p; k++) {
    const cy = H * 0.42 + (k - shift) * row;
    const fadeTop = clamp01((cy - H * 0.42 + row) / row);
    const born = easeOut((t - list[k].start) / 0.3);
    const alpha = fadeTop * born;
    if (alpha <= 0.01) continue;
    const { singer } = singerAt(scene, list[k].start + 0.01);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.save();
    ctx.translate(cx, cy);
    const pop = 0.7 + 0.3 * born;
    ctx.scale(pop, pop);
    ctx.beginPath();
    ctx.arc(0, 0, R, 0, Math.PI * 2);
    ctx.fillStyle = ring;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(0, 0, R - ringW, 0, Math.PI * 2);
    ctx.fillStyle = "#000000";
    ctx.fill();
    circlePhoto(ctx, singer, 0, 0, inner);
    ctx.restore();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `700 ${Math.round(44 * u)}px ${SANS}`;
    ctx.fillText(singer?.name ?? "", tx, cy - 70 * u, W - tx - 50 * u);
    ctx.font = `400 ${Math.round(33 * u)}px ${SANS}`;
    ctx.fillStyle = scene.project.captions.color;
    const text = k === p ? sungSoFar(scene.words, list[k], t) : list[k].text;
    wrap(ctx, text, W - tx - 60 * u)
      .slice(0, 2)
      .forEach((line, j) => ctx.fillText(line, tx, cy - 14 * u + j * 42 * u));
    ctx.restore();
  }
}

// ── Karaoke ────────────────────────────────────────────────────────────────
// Proper karaoke: the voice's photo as a deep blue backdrop, two big centred
// lines in a heavy outline, the one being sung wiping pink to orange word by
// word at each word's own pace, a ball bouncing from word to word, and a
// three-dot countdown before a line that follows a long gap.

/** The colour a wipe or accent shows in: the caption colour, unless it's white. */
const accentOf = (scene: Scene, fallback: string) => {
  const c = scene.project.captions.color.toUpperCase();
  return c === "#FFFFFF" || c === "#FFF"
    ? fallback
    : scene.project.captions.color;
};

type PlacedWord = {
  text: string;
  x: number;
  y: number;
  w: number;
  start: number;
  end: number;
};

/** Lays a phrase's words out in wrapped, centred rows at the context's font. */
function placeWords(
  ctx: Ctx,
  words: CaptionWord[],
  ph: Phrase,
  width: number,
  lh: number,
): { placed: PlacedWord[]; rows: number } {
  const space = ctx.measureText(" ").width;
  const rows: PlacedWord[][] = [[]];
  let x = 0;
  for (const w of words.slice(ph.first, ph.last + 1)) {
    const ww = ctx.measureText(w.text).width;
    if (x > 0 && x + ww > width) {
      rows.push([]);
      x = 0;
    }
    rows[rows.length - 1].push({
      text: w.text,
      x,
      y: 0,
      w: ww,
      start: w.start,
      end: w.end,
    });
    x += ww + space;
  }
  const placed: PlacedWord[] = [];
  rows.forEach((row, r) => {
    const last = row[row.length - 1];
    const rowW = last ? last.x + last.w : 0;
    row.forEach((w) =>
      placed.push({ ...w, x: w.x + (width - rowW) / 2, y: r * lh }),
    );
  });
  return { placed, rows: rows.length };
}

const COUNTDOWN_GAP = 2.5;

function drawKaraoke(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  const u = unit(W, H);
  // Tint whatever's behind (the voice's photo by default) karaoke blue.
  const tint = ctx.createLinearGradient(0, 0, 0, H);
  tint.addColorStop(0, "rgba(16,22,110,0.72)");
  tint.addColorStop(1, "rgba(52,10,96,0.8)");
  ctx.fillStyle = tint;
  ctx.fillRect(0, 0, W, H);

  // The song and voice, small at the top.
  const now = singerAt(scene, t);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `700 ${Math.round(30 * u)}px ${SANS}`;
  const head = `♪ ${songTitle(scene.title)} · ${now.singer?.name ?? ""}`;
  const hw = Math.min(W * 0.84, ctx.measureText(head).width + 56 * u);
  ctx.fillStyle = "rgba(255,255,255,0.14)";
  ctx.beginPath();
  ctx.roundRect(W / 2 - hw / 2, 70 * u, hw, 62 * u, 31 * u);
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(head, W / 2, 101 * u, hw - 40 * u);

  if (!lyricsOn(scene)) return;
  const list = phrases(scene.words);
  let p = phraseIndexAt(list, t);
  // Before a line starts (in a gap), show it as the line coming up.
  if (p + 1 < list.length && (p < 0 || t > list[p].end + 0.4)) p = p + 1;
  if (p < 0) return;
  const size = Math.round(Math.min(96 * u, W * 0.085));
  ctx.font = `800 ${size}px ${SANS}`;
  const lh = size * 1.18;
  const width = W * 0.84;
  const left = (W - width) / 2;
  const cur = placeWords(ctx, scene.words, list[p], width, lh);
  const curTop = H * 0.47 - (cur.rows * lh) / 2;
  const wipeA = accentOf(scene, "#FF4FA3");
  const grad = ctx.createLinearGradient(left, 0, left + width, 0);
  grad.addColorStop(0, wipeA);
  grad.addColorStop(1, "#FFB23F");

  const outlined = (
    text: string,
    x: number,
    y: number,
    fill: string | CanvasGradient,
  ) => {
    ctx.lineJoin = "round";
    ctx.lineWidth = 13 * u;
    ctx.strokeStyle = "#14062E";
    ctx.strokeText(text, x, y);
    ctx.fillStyle = fill;
    ctx.fillText(text, x, y);
  };

  // The line being sung: white, wiping into colour.
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  // A line comes up as soon as the one before it is done (or three seconds
  // ahead of a long gap), so the screen is never empty between lines.
  const showAt = Math.max(
    p > 0 ? list[p - 1].end + 0.15 : scene.project.trim.start,
    list[p].start - 3,
  );
  // A line that's already up when the clip starts is simply there.
  const born =
    showAt <= scene.project.trim.start ? 1 : easeOut((t - showAt) / 0.3);
  ctx.globalAlpha = born;
  for (const w of cur.placed) {
    const x = left + w.x,
      y = curTop + w.y + lh / 2;
    outlined(w.text, x, y, "#FFFFFF");
    const done = clamp01((t - w.start) / Math.max(0.08, w.end - w.start));
    if (done <= 0) continue;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x - 8 * u, y - lh / 2, (w.w + 16 * u) * done, lh);
    ctx.clip();
    ctx.shadowColor = wipeA;
    ctx.shadowBlur = 24 * u;
    ctx.fillStyle = grad;
    ctx.fillText(w.text, x, y);
    ctx.restore();
  }

  // The ball: on the word being sung, hopping to the next as it goes.
  const ws = cur.placed;
  if (ws.length && t >= showAt) {
    let i = 0;
    for (let k = 0; k < ws.length; k++) if (ws[k].start <= t) i = k;
    const a = ws[i],
      b = ws[Math.min(ws.length - 1, i + 1)];
    const f =
      t < ws[0].start
        ? 0
        : clamp01(
            (t - a.start) /
              Math.max(0.12, (b === a ? a.end : b.start) - a.start),
          );
    const ax = left + a.x + a.w / 2,
      bx = left + b.x + b.w / 2;
    const x = ax + (bx - ax) * easeInOut(f);
    const rowY = curTop + (f < 0.5 ? a.y : b.y);
    const hop = Math.abs(Math.sin(f * Math.PI)) * 46 * u;
    const y = rowY - 18 * u - hop;
    ctx.save();
    ctx.shadowColor = "#FFE24D";
    ctx.shadowBlur = 20 * u;
    ctx.fillStyle = "#FFE24D";
    ctx.beginPath();
    ctx.arc(x, y, 15 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // A countdown before a line that follows a long gap.
  const gapBefore =
    p > 0
      ? list[p].start - list[p - 1].end
      : list[p].start - scene.project.trim.start;
  const left3 = list[p].start - t;
  if (gapBefore >= COUNTDOWN_GAP && left3 > 0 && left3 <= 2.4) {
    const dots = Math.ceil(left3 / 0.8);
    for (let k = 0; k < 3; k++) {
      ctx.globalAlpha = born * (k < dots ? 1 : 0.2);
      ctx.fillStyle = "#FFE24D";
      ctx.beginPath();
      ctx.arc(
        left + cur.placed[0].x + 14 * u + k * 44 * u,
        curTop - 44 * u,
        12 * u,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;

  // The next line, dimmer, below.
  if (p + 1 < list.length) {
    ctx.font = `800 ${Math.round(size * 0.8)}px ${SANS}`;
    const next = placeWords(ctx, scene.words, list[p + 1], width, lh * 0.8);
    const nextTop = curTop + cur.rows * lh + lh * 0.45;
    ctx.globalAlpha = 0.55 * born;
    for (const w of next.placed)
      outlined(w.text, left + w.x, nextTop + w.y + (lh * 0.8) / 2, "#FFFFFF");
    ctx.globalAlpha = 1;
  }
}

// ── Poster ─────────────────────────────────────────────────────────────────
// Each line fills the frame as huge stacked type: words grouped into rows,
// each row sized to the frame's width, weights and colours alternating. A
// word slams in when it's sung. Every line gets its own colour scheme, and
// the frame wipes to it as the line starts.

const POSTER_SCHEMES: { bg: string; a: string; b: string }[] = [
  { bg: "#2E0F52", a: "#FFFFFF", b: "#D1FE17" },
  { bg: "#D1FE17", a: "#2E0F52", b: "#782DC8" },
  { bg: "#782DC8", a: "#FFFFFF", b: "#FFD1F0" },
  { bg: "#0E0E12", a: "#FFFFFF", b: "#FF4D8D" },
  { bg: "#FF4D8D", a: "#FFFFFF", b: "#2E0F52" },
];

/** A small, stable pseudo-random number for line `k` (layout varies per line, not per frame). */
const seeded = (k: number, salt: number) => {
  const x = Math.sin(k * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

function drawPoster(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  const u = unit(W, H);
  const list = lyricsOn(scene) ? phrases(scene.words) : [];
  const p = phraseIndexAt(list, t);
  const scheme =
    POSTER_SCHEMES[
      (Math.max(0, p) + POSTER_SCHEMES.length) % POSTER_SCHEMES.length
    ];
  const prev =
    POSTER_SCHEMES[
      (Math.max(0, p - 1) + POSTER_SCHEMES.length) % POSTER_SCHEMES.length
    ];
  // The new colour wipes up from the bottom as the line starts.
  const into = p >= 0 ? easeInOut((t - list[p].start + 0.05) / 0.3) : 1;
  ctx.fillStyle = p > 0 ? prev.bg : scheme.bg;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = scheme.bg;
  ctx.fillRect(0, H * (1 - into), W, H * into);
  if (p < 0) {
    ctx.fillStyle = scheme.a;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `400 ${Math.round(110 * u)}px ${DISPLAY}`;
    ctx.fillText(songTitle(scene.title).toUpperCase(), W / 2, H / 2, W * 0.86);
    return;
  }
  const ph = list[p];
  const words = scene.words.slice(ph.first, ph.last + 1);
  // Rows of one to three words, chosen per line.
  const rows: CaptionWord[][] = [];
  let i = 0,
    n = 0;
  while (i < words.length) {
    const take = Math.min(
      words.length - i,
      1 + Math.floor(seeded(p, n) * (words.length > 6 ? 3 : 2)),
    );
    rows.push(words.slice(i, i + take));
    i += take;
    n++;
  }
  // Each row as wide as the frame allows; the stack scaled to fit the height.
  const maxW = W * 0.86;
  const fonts = rows.map((_, r) =>
    (r + p) % 2 === 0
      ? (s: number) => `400 ${s}px ${DISPLAY}`
      : (s: number) => `800 ${s}px ${SANS}`,
  );
  const sizes = rows.map((row, r) => {
    const text = row
      .map((w) => w.text)
      .join(" ")
      .toUpperCase();
    ctx.font = fonts[r](100);
    const w100 = Math.max(1, ctx.measureText(text).width);
    return Math.min(420 * u, (100 * maxW) / w100);
  });
  const lineGap = 0.92;
  let total = sizes.reduce((a, s) => a + s * lineGap, 0);
  const fit = Math.min(1, (H * 0.78) / total);
  total *= fit;
  let y = (H - total) / 2;
  const alignLeft = seeded(p, 9) > 0.5;
  // The whole stack breathes with the music.
  const pulse = 1 + 0.035 * loudnessAt(scene, t);
  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.scale(pulse, pulse);
  ctx.translate(-W / 2, -H / 2);
  ctx.textBaseline = "top";
  rows.forEach((row, r) => {
    const size = sizes[r] * fit;
    // Some rows tilt a touch; some are outlines only.
    const tilt = (seeded(p, r + 31) - 0.5) * 0.09;
    const hollow = seeded(p, r + 57) > 0.68;
    ctx.font = fonts[r](size);
    ctx.textAlign = "left";
    const text = row.map((w) => w.text.toUpperCase());
    const space = ctx.measureText(" ").width;
    const widths = text.map((w) => ctx.measureText(w).width);
    const rowW = widths.reduce((a, b) => a + b, 0) + space * (text.length - 1);
    let x = alignLeft ? W * 0.07 : (W - rowW) / 2;
    row.forEach((w, j) => {
      const hit = clamp01((t - w.start) / 0.18);
      if (t >= w.start - 0.02) {
        // The slam: big and transparent to its size, with a little overshoot.
        const k = hit < 1 ? 1 + 0.5 * Math.pow(1 - hit, 2) : 1;
        ctx.save();
        ctx.globalAlpha = clamp01(hit * 2.5);
        ctx.translate(W / 2, y + size / 2);
        ctx.rotate(tilt);
        ctx.translate(x + widths[j] / 2 - W / 2, 0);
        ctx.scale(k, k);
        const ink = (r + j + p) % 3 === 1 ? scheme.b : scheme.a;
        if (hollow) {
          ctx.lineJoin = "round";
          ctx.lineWidth = Math.max(3 * u, size * 0.035);
          ctx.strokeStyle = ink;
          ctx.strokeText(text[j], -widths[j] / 2, -size / 2);
        } else {
          ctx.fillStyle = ink;
          ctx.fillText(text[j], -widths[j] / 2, -size / 2);
        }
        ctx.restore();
      }
      x += widths[j] + space;
    });
    y += size * lineGap;
  });
  ctx.restore();
}

// ── Call ───────────────────────────────────────────────────────────────────
// A video call from the voice: their photo full screen, their name and a
// running call timer at the top, the line being sung as a live caption, the
// call controls at the bottom. With several voices the screen splits, and
// the one singing gets the green "speaking" ring.

const RING_SECS = 1.6;

function drawCall(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  const u = unit(W, H);
  const since = Math.max(0, t - scene.project.trim.start);
  const level = loudnessAt(scene, t);
  const now = singerAt(scene, t);
  const voices = scene.singers.length ? scene.singers.slice(0, 4) : [null];
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, W, H);
  // The tiles: one per voice, stacked (portrait) or side by side (wide).
  const wide = W > H;
  const n = voices.length;
  const g = n > 1 ? 10 * u : 0;
  voices.forEach((v, i) => {
    const tw = wide ? (W - g * (n - 1)) / n : W,
      th = wide ? H : (H - g * (n - 1)) / n;
    const tx = wide ? i * (tw + g) : 0,
      ty = wide ? 0 : i * (th + g);
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(tx, ty, tw, th, n > 1 ? 34 * u : 0);
    ctx.clip();
    photoIn(ctx, v, tx, ty, tw, th);
    if (n > 1 && i !== now.index) {
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fillRect(tx, ty, tw, th);
    }
    ctx.restore();
    if (n > 1 && i === now.index) {
      ctx.strokeStyle = "#30D158";
      ctx.lineWidth = (6 + 10 * level) * u;
      ctx.beginPath();
      ctx.roundRect(tx + 4 * u, ty + 4 * u, tw - 8 * u, th - 8 * u, 32 * u);
      ctx.stroke();
    }
    if (n > 1) {
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.font = `700 ${Math.round(30 * u)}px ${SANS}`;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText(v?.name ?? "", tx + 28 * u, ty + th - 28 * u, tw - 56 * u);
    }
  });
  // Darken top and bottom so the overlay reads.
  const top = ctx.createLinearGradient(0, 0, 0, H * 0.28);
  top.addColorStop(0, "rgba(0,0,0,0.6)");
  top.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, W, H * 0.28);
  const bottom = ctx.createLinearGradient(0, H * 0.6, 0, H);
  bottom.addColorStop(0, "rgba(0,0,0,0)");
  bottom.addColorStop(1, "rgba(0,0,0,0.7)");
  ctx.fillStyle = bottom;
  ctx.fillRect(0, H * 0.6, W, H * 0.4);

  // Name and timer.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `800 ${Math.round(52 * u)}px ${SANS}`;
  const who =
    n > 1
      ? scene.singers.map((s) => s.name).join(" & ")
      : (now.singer?.name ?? "");
  ctx.fillText(who, W / 2, 110 * u, W * 0.86);
  ctx.globalAlpha = 0.8;
  ctx.font = `600 ${Math.round(32 * u)}px ${SANS}`;
  ctx.fillText(
    since < RING_SECS ? "Calling…" : `Video call · ${mmss(since - RING_SECS)}`,
    W / 2,
    166 * u,
  );
  ctx.globalAlpha = 1;

  // You, in the corner (one voice): a small tile with the song on it.
  if (n === 1) {
    const pw = Math.min(W, H) * 0.24,
      ph = pw * 1.4;
    const px = W - pw - 40 * u,
      py = 220 * u;
    const g2 = ctx.createLinearGradient(px, py, px + pw, py + ph);
    g2.addColorStop(0, "#782DC8");
    g2.addColorStop(1, "#2E0F52");
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 24 * u;
    ctx.fillStyle = g2;
    ctx.beginPath();
    ctx.roundRect(px, py, pw, ph, 26 * u);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.font = `800 ${Math.round(44 * u)}px ${SANS}`;
    ctx.fillText("♪", px + pw / 2, py + ph * 0.42);
    ctx.font = `700 ${Math.round(22 * u)}px ${SANS}`;
    ctx.fillText(
      songTitle(scene.title),
      px + pw / 2,
      py + ph * 0.7,
      pw - 24 * u,
    );
  }

  // Controls: mute, flip, end.
  const cy = H - 120 * u;
  const btn = 50 * u;
  [-1, 0, 1].forEach((k) => {
    const cx = W / 2 + k * 170 * u;
    ctx.beginPath();
    ctx.arc(cx, cy, btn, 0, Math.PI * 2);
    ctx.fillStyle = k === 1 ? "#FF3B30" : "rgba(255,255,255,0.22)";
    ctx.fill();
    ctx.strokeStyle = "#FFFFFF";
    ctx.fillStyle = "#FFFFFF";
    ctx.lineWidth = 5 * u;
    ctx.lineCap = "round";
    if (k === -1) {
      // A mic.
      ctx.beginPath();
      ctx.roundRect(cx - 10 * u, cy - 24 * u, 20 * u, 32 * u, 10 * u);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, 18 * u, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx, cy + 18 * u);
      ctx.lineTo(cx, cy + 26 * u);
      ctx.stroke();
    } else if (k === 0) {
      // A camera.
      ctx.beginPath();
      ctx.roundRect(cx - 24 * u, cy - 15 * u, 32 * u, 30 * u, 6 * u);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx + 10 * u, cy - 4 * u);
      ctx.lineTo(cx + 24 * u, cy - 13 * u);
      ctx.lineTo(cx + 24 * u, cy + 13 * u);
      ctx.lineTo(cx + 10 * u, cy + 4 * u);
      ctx.closePath();
      ctx.fill();
    } else {
      // The handset, hung up.
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(Math.PI * 0.75);
      ctx.lineWidth = 11 * u;
      ctx.beginPath();
      ctx.arc(0, 8 * u, 22 * u, Math.PI * 1.15, Math.PI * 1.85);
      ctx.stroke();
      ctx.restore();
    }
  });

  // The live caption.
  if (lyricsOn(scene)) {
    const list = phrases(scene.words);
    const p = phraseIndexAt(list, t);
    if (p >= 0 && t <= list[p].end + 1.2) {
      const text = sungSoFar(scene.words, list[p], t);
      if (text) {
        const size = Math.round(40 * u);
        ctx.font = `700 ${size}px ${SANS}`;
        const lines = wrap(ctx, text, W * 0.72).slice(-2);
        const lh = size * 1.3;
        const bw = Math.min(
          W * 0.84,
          Math.max(...lines.map((l) => ctx.measureText(l).width)) + 64 * u,
        );
        const bh = lines.length * lh + 36 * u;
        const by = cy - btn - 50 * u - bh;
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.beginPath();
        ctx.roundRect(W / 2 - bw / 2, by, bw, bh, 24 * u);
        ctx.fill();
        ctx.fillStyle = scene.project.captions.color;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        lines.forEach((l, j) =>
          ctx.fillText(l, W / 2, by + 18 * u + lh * (j + 0.5)),
        );
      }
    }
  }

  // Ringing: the call connecting, over everything for the first moment.
  if (since < RING_SECS + 0.4) {
    const fadeOut = 1 - easeInOut((since - RING_SECS) / 0.4);
    ctx.save();
    ctx.globalAlpha = fadeOut;
    ctx.fillStyle = "rgba(10,6,24,0.88)";
    ctx.fillRect(0, 0, W, H);
    const r = Math.min(W, H) * 0.16;
    for (let k = 0; k < 3; k++) {
      const ph = (since * 0.9 + k / 3) % 1;
      ctx.strokeStyle = `rgba(255,255,255,${0.35 * (1 - ph)})`;
      ctx.lineWidth = 4 * u;
      ctx.beginPath();
      ctx.arc(W / 2, H * 0.42, r * (1 + ph * 0.9), 0, Math.PI * 2);
      ctx.stroke();
    }
    circlePhoto(ctx, now.singer, W / 2, H * 0.42, r);
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `800 ${Math.round(54 * u)}px ${SANS}`;
    ctx.fillText(
      now.singer?.name ?? "",
      W / 2,
      H * 0.42 + r + 80 * u,
      W * 0.86,
    );
    ctx.globalAlpha = fadeOut * 0.75;
    ctx.font = `600 ${Math.round(32 * u)}px ${SANS}`;
    ctx.fillText("Calling…", W / 2, H * 0.42 + r + 136 * u);
    ctx.restore();
  }
}

// ── Notes ──────────────────────────────────────────────────────────────────
// A notes app in dark mode: the song as the note's title, today's date, the
// voice's photo pasted in, and the lyrics typing themselves out as they're
// sung, a cursor blinking at the end. The page scrolls up as it fills.

const NOTES_YELLOW = "#FFD60A";

function drawNotes(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  const u = unit(W, H);
  const since = t - scene.project.trim.start;
  ctx.fillStyle = "#1C1C1E";
  ctx.fillRect(0, 0, W, H);
  const mx = Math.max(56 * u, W * 0.06);
  const colW = Math.min(W - mx * 2, 1000 * u);
  const left = (W - colW) / 2;

  // The page, laid out top to bottom, then scrolled.
  const titleSize = Math.round(64 * u),
    bodySize = Math.round(46 * u),
    lh = bodySize * 1.38;
  ctx.font = `800 ${titleSize}px ${SANS}`;
  const titleLines = wrap(ctx, songTitle(scene.title), colW);
  const headH = 150 * u;
  let y = headH + 30 * u;
  const titleY = y;
  y += titleLines.length * titleSize * 1.15 + 14 * u;
  const dateY = y;
  y += 70 * u;
  const now = singerAt(scene, t);
  const photoH = Math.min(colW * 0.5, H * 0.22);
  const photoY = y;
  y += photoH + 50 * u;

  // The lyrics so far: finished lines whole, the current one as typed.
  ctx.font = `400 ${bodySize}px ${SANS}`;
  const rows: string[] = [];
  let caret: { row: number; x: number } | null = null;
  let curFrom = 0,
    prevFrom = 0,
    lineStart = 0;
  if (lyricsOn(scene)) {
    const list = phrases(scene.words);
    const p = phraseIndexAt(list, t);
    // The note starts with the clip: lines sung before it aren't on the page.
    const from = list.findIndex((ph) => ph.end >= scene.project.trim.start);
    for (let k = Math.max(0, from); k <= p; k++) {
      const ph = list[k];
      let text = ph.text;
      if (k === p) {
        // Typed up to the word being sung, part of it by how far it's got.
        const ws = scene.words.slice(ph.first, ph.last + 1);
        text = ws
          .map((w) => {
            const done = clamp01(
              (t - w.start) / Math.max(0.06, w.end - w.start),
            );
            return w.text.slice(0, Math.ceil(w.text.length * done));
          })
          .filter(Boolean)
          .join(" ");
      }
      const lines = text ? wrap(ctx, text, colW) : [""];
      if (k === p) {
        prevFrom = curFrom;
        curFrom = rows.length;
        lineStart = ph.start;
      } else curFrom = rows.length;
      rows.push(...lines);
      if (k === p)
        caret = {
          row: rows.length - 1,
          x: ctx.measureText(lines[lines.length - 1]).width,
        };
    }
  }
  const bodyTop = y;
  // The line being typed sits in the middle of the screen: as each new line
  // starts the page eases up to it, then holds while it's typed.
  const centreOf = (row: number) => bodyTop + row * lh + lh / 2 - H / 2;
  const scroll = Math.max(
    0,
    centreOf(prevFrom) +
      (centreOf(curFrom) - centreOf(prevFrom)) *
        easeInOut((t - lineStart) / 0.4),
  );

  ctx.save();
  ctx.translate(0, -scroll);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `800 ${titleSize}px ${SANS}`;
  titleLines.forEach((l, j) =>
    ctx.fillText(l, left, titleY + j * titleSize * 1.15),
  );
  ctx.fillStyle = "#8E8E93";
  ctx.font = `500 ${Math.round(30 * u)}px ${SANS}`;
  const d = new Date();
  ctx.fillText(
    `${d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`,
    left,
    dateY,
  );
  // The pasted photo.
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(left, photoY, photoH * 1.2, photoH, 22 * u);
  ctx.clip();
  photoIn(ctx, now.singer, left, photoY, photoH * 1.2, photoH);
  ctx.restore();
  ctx.fillStyle =
    scene.project.captions.color.toUpperCase() === "#FFFFFF"
      ? "#F2F2F7"
      : scene.project.captions.color;
  ctx.font = `400 ${bodySize}px ${SANS}`;
  // The line being typed, highlighted like a selection.
  if (caret) {
    for (let j = curFrom; j <= caret.row; j++) {
      const w = ctx.measureText(rows[j]).width;
      if (!w) continue;
      ctx.fillStyle = "rgba(255,214,10,0.2)";
      ctx.beginPath();
      ctx.roundRect(
        left - 8 * u,
        bodyTop + j * lh - 4 * u,
        w + 16 * u,
        bodySize * 1.25,
        8 * u,
      );
      ctx.fill();
    }
  }
  ctx.fillStyle =
    scene.project.captions.color.toUpperCase() === "#FFFFFF"
      ? "#F2F2F7"
      : scene.project.captions.color;
  rows.forEach((l, j) => ctx.fillText(l, left, bodyTop + j * lh));
  // The caret, blinking.
  if (caret && Math.floor(since * 2) % 2 === 0) {
    ctx.fillStyle = NOTES_YELLOW;
    ctx.fillRect(
      left + caret.x + 4 * u,
      bodyTop + caret.row * lh + 2 * u,
      5 * u,
      bodySize * 1.1,
    );
  }
  ctx.restore();

  // The bar on top (drawn over the scrolled page).
  ctx.fillStyle = "#1C1C1E";
  ctx.fillRect(0, 0, W, headH);
  ctx.fillStyle = NOTES_YELLOW;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.font = `600 ${Math.round(40 * u)}px ${SANS}`;
  ctx.fillText("‹ Notes", left - 10 * u, headH * 0.6);
  ctx.textAlign = "right";
  ctx.font = `700 ${Math.round(40 * u)}px ${SANS}`;
  ctx.fillText("Done", left + colW, headH * 0.6);

  // The toolbar at the bottom.
  const tb = H - 120 * u;
  ctx.fillStyle = "#1C1C1E";
  ctx.fillRect(0, tb - 40 * u, W, H - tb + 40 * u);
  ctx.strokeStyle = NOTES_YELLOW;
  ctx.fillStyle = NOTES_YELLOW;
  ctx.lineWidth = 5 * u;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const slots = [0.12, 0.37, 0.63, 0.88].map((f) => left + colW * f);
  // A checklist, a camera, a pen, compose.
  const s = 22 * u;
  ctx.beginPath();
  ctx.arc(slots[0] - s * 0.6, tb, s * 0.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(slots[0] + s * 0.2, tb);
  ctx.lineTo(slots[0] + s * 1.3, tb);
  ctx.stroke();
  ctx.beginPath();
  ctx.roundRect(slots[1] - s, tb - s * 0.7, s * 2, s * 1.5, 6 * u);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(slots[1], tb + s * 0.05, s * 0.4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(slots[2] - s * 0.8, tb + s * 0.8);
  ctx.lineTo(slots[2] + s * 0.7, tb - s * 0.7);
  ctx.stroke();
  ctx.beginPath();
  ctx.roundRect(slots[3] - s * 0.9, tb - s * 0.8, s * 1.6, s * 1.6, 5 * u);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(slots[3] - s * 0.1, tb + s * 0.1);
  ctx.lineTo(slots[3] + s * 0.9, tb - s * 0.9);
  ctx.stroke();
}

/** The template's own layer (everything but the background, captions and mark). */
export function drawTemplate(
  ctx: Ctx,
  W: number,
  H: number,
  scene: Scene,
  t: number,
  drawSpotlight: () => void,
) {
  const id = scene.project.template;
  if (id === "orbit") drawOrbit(ctx, W, H, scene, t);
  else if (id === "glide")
    drawGlide(ctx, W, H, scene, t, songTitle(scene.title));
  else if (id === "neon") drawNeon(ctx, W, H, scene, t);
  else if (id === "scroll") drawScroll(ctx, W, H, scene, t);
  else if (id === "chat") drawChat(ctx, W, H, scene, t);
  else if (id === "karaoke") drawKaraoke(ctx, W, H, scene, t);
  else if (id === "poster") drawPoster(ctx, W, H, scene, t);
  else if (id === "call") drawCall(ctx, W, H, scene, t);
  else if (id === "notes") drawNotes(ctx, W, H, scene, t);
  else drawSpotlight();
}
